import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { toast } from "sonner";
import { Reorder, useDragControls } from "framer-motion";
import { ArrowLeft, ArrowRight, Check, Swords, Users, ListOrdered, Plus, Trash2, X, Search, Loader2, ShieldCheck, Trophy, GripVertical, CalendarDays } from "lucide-react";
import AppHeader from "@/components/AppHeader";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import { CW_LINE_TYPES, CW_SERIES, CW_SCHEDULE_MODES, isDoublesLine, uid } from "@/lib/clubwars";
import api, { formatApiError } from "@/lib/api";

const inputCls = "w-full rounded-xl bg-surface glow-border px-3 py-2.5 text-sm outline-none focus:ring-2 focus:ring-lime-400";
const STEPS = [
  { id: 1, label: "Details", Icon: Swords },
  { id: 2, label: "Teams & Rosters", Icon: Users },
  { id: 3, label: "Match Categories", Icon: ListOrdered },
];

const emptyForm = {
  name: "", venue: "", start_date: "", end_date: "",
  umpires_enabled: true, sweep_bonus: 0, best_of: 3, courts: 1, schedule_mode: "by_round", lineup_deadline: "",
  teams: [], lines: [],
};

export default function ClubWarsWizard() {
  const nav = useNavigate();
  const { id } = useParams();
  const editing = !!id;
  const [step, setStep] = useState(1);
  const [form, setForm] = useState(emptyForm);
  const [busy, setBusy] = useState(false);
  const [loading, setLoading] = useState(editing);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  useEffect(() => {
    if (!editing) return;
    let active = true;
    api.get(`/club-wars/${id}`).then(({ data }) => { if (active) { setForm({ ...emptyForm, ...data }); setLoading(false); } })
      .catch(() => { toast.error("Could not load this Club War"); nav("/club-wars"); });
    return () => { active = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id, editing]);

  // ---- teams ----
  const addTeam = () => set("teams", [...form.teams, { id: uid("team"), name: `Team ${form.teams.length + 1}`, players: [] }]);
  const removeTeam = (tid) => set("teams", form.teams.filter((t) => t.id !== tid));
  const patchTeam = (tid, patch) => set("teams", form.teams.map((t) => (t.id === tid ? { ...t, ...patch } : t)));
  const addPlayer = (tid, player) => {
    const t = form.teams.find((x) => x.id === tid);
    if (!t) return;
    patchTeam(tid, { players: [...t.players, { id: uid("ply"), ...player }] });
  };
  const removePlayer = (tid, pid) => {
    const t = form.teams.find((x) => x.id === tid);
    patchTeam(tid, { players: t.players.filter((p) => p.id !== pid) });
  };

  // ---- lines ----
  const addLine = () => set("lines", [...form.lines, { id: uid("line"), type: "mens_singles", skill: "", entries: 1, series: "single", format: "single", points: 1, set_target: 11, set_win_by: 2, set_cap: 0 }]);
  const removeLine = (lid) => set("lines", form.lines.filter((l) => l.id !== lid));
  const patchLine = (lid, patch) => set("lines", form.lines.map((l) => (l.id === lid ? { ...l, ...patch } : l)));

  const canNext = () => (step === 1 ? form.name.trim().length > 0 : true);

  const save = async () => {
    if (!form.name.trim()) { toast.error("Event name is required"); setStep(1); return; }
    setBusy(true);
    const payload = {
      name: form.name, venue: form.venue, start_date: form.start_date || null, end_date: form.end_date || null,
      umpires_enabled: !!form.umpires_enabled, sweep_bonus: Number(form.sweep_bonus) || 0, best_of: Number(form.best_of) || 3,
      courts: Number(form.courts) || 1, schedule_mode: form.schedule_mode || "by_round",
      lineup_deadline: form.lineup_deadline ? new Date(form.lineup_deadline).toISOString() : null,
      teams: form.teams, lines: form.lines,
    };
    try {
      if (editing) { await api.patch(`/club-wars/${id}`, payload); toast.success("Club War saved"); nav(`/club-wars/${id}`); }
      else { const { data } = await api.post("/club-wars", payload); toast.success("Club War created"); nav(`/club-wars/${data.cw_id}`); }
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Could not save"); }
    finally { setBusy(false); }
  };

  if (loading) return <div className="min-h-screen flex items-center justify-center" style={{ background: "var(--bg)" }}><Loader2 className="w-6 h-6 animate-spin text-primary-c" /></div>;

  return (
    <div className="min-h-screen" style={{ background: "var(--bg)", color: "var(--text)" }}>
      <AppHeader title={editing ? "Edit Club War" : "New Club War"} />
      <div className="max-w-3xl mx-auto px-4 sm:px-6 py-10 grain">
        <button data-testid="cw-wizard-back" onClick={() => nav("/club-wars")} className="text-sm flex items-center gap-2 mb-6" style={{ color: "var(--text-2)" }}>
          <ArrowLeft className="w-4 h-4" /> All Club Wars
        </button>

        <div className="flex items-center gap-2 mb-8" data-testid="cw-stepper">
          {STEPS.map((s, i) => (
            <div key={s.id} className="flex items-center gap-2 flex-1">
              <div className="flex items-center gap-2">
                <div className="w-9 h-9 rounded-full flex items-center justify-center shrink-0 transition-colors"
                  style={step >= s.id ? { background: "var(--primary)", color: "var(--on-primary)" } : { background: "var(--surface)", color: "var(--text-2)", border: "1px solid var(--border)" }}>
                  {step > s.id ? <Check className="w-4 h-4" /> : <s.Icon className="w-4 h-4" />}
                </div>
                <span className="text-xs font-bold hidden sm:block" style={{ color: step >= s.id ? "var(--text)" : "var(--text-2)" }}>{s.label}</span>
              </div>
              {i < STEPS.length - 1 && <div className="flex-1 h-px" style={{ background: "var(--border)" }} />}
            </div>
          ))}
        </div>

        {step === 1 && (
          <div className="space-y-4" data-testid="cw-step-details">
            <div>
              <label className="text-[10px] uppercase font-bold" style={{ color: "var(--text-2)" }}>Event name</label>
              <input data-testid="cw-name" value={form.name} onChange={(e) => set("name", e.target.value)} placeholder="e.g. Tagum Aces vs Davao Strikers" className={`${inputCls} mt-1`} style={{ color: "var(--text)" }} />
            </div>
            <div>
              <label className="text-[10px] uppercase font-bold" style={{ color: "var(--text-2)" }}>Venue</label>
              <input data-testid="cw-venue" value={form.venue} onChange={(e) => set("venue", e.target.value)} placeholder="Venue / club" className={`${inputCls} mt-1`} style={{ color: "var(--text)" }} />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div><label className="text-[10px] uppercase font-bold" style={{ color: "var(--text-2)" }}>Start date</label><input data-testid="cw-start" type="date" value={form.start_date || ""} onChange={(e) => set("start_date", e.target.value)} className={`${inputCls} mt-1`} style={{ color: "var(--text)" }} /></div>
              <div><label className="text-[10px] uppercase font-bold" style={{ color: "var(--text-2)" }}>End date</label><input data-testid="cw-end" type="date" value={form.end_date || ""} onChange={(e) => set("end_date", e.target.value)} className={`${inputCls} mt-1`} style={{ color: "var(--text)" }} /></div>
            </div>
            <div>
              <label className="text-[10px] uppercase font-bold" style={{ color: "var(--text-2)" }}>Courts available</label>
              <input data-testid="cw-courts" type="number" min="1" max="50" value={form.courts} onChange={(e) => set("courts", Math.max(1, Number(e.target.value) || 1))} className={`${inputCls} mt-1`} style={{ color: "var(--text)" }} />
              <p className="text-[11px] mt-1" style={{ color: "var(--text-2)" }}>How many courts this event runs on — used to schedule matches across courts.</p>
            </div>
            <div className="rounded-xl glow-border p-4 flex items-center justify-between" style={{ background: "var(--surface)" }}>
              <div className="flex items-center gap-3">
                <ShieldCheck className="w-5 h-5 text-primary-c" />
                <div>
                  <div className="font-bold text-sm">Does this meet have umpires?</div>
                  <div className="text-xs" style={{ color: "var(--text-2)" }}>{form.umpires_enabled ? "Credentialed umpire scoring (coin toss, serving bands, fault engine)." : "Self-officiated — court monitors log final scores directly."}</div>
                </div>
              </div>
              <Switch data-testid="cw-umpires-toggle" checked={!!form.umpires_enabled} onCheckedChange={(v) => set("umpires_enabled", v)} />
            </div>
            <div>
              <label className="text-[10px] uppercase font-bold" style={{ color: "var(--text-2)" }}>Line-up deadline (optional)</label>
              <input data-testid="cw-deadline" type="datetime-local" value={(form.lineup_deadline || "").slice(0, 16)} onChange={(e) => set("lineup_deadline", e.target.value)} className={`${inputCls} mt-1`} style={{ color: "var(--text)" }} />
              <p className="text-[11px] mt-1" style={{ color: "var(--text-2)" }}>Captains can submit line-ups via their shareable link until this time.</p>
            </div>
          </div>
        )}

        {step === 2 && (
          <div className="space-y-4" data-testid="cw-step-teams">
            <div className="flex items-center justify-between">
              <h3 className="font-display font-bold text-lg">Teams & rosters</h3>
              <button data-testid="cw-add-team" onClick={addTeam} className="glass rounded-full px-3 py-1.5 text-xs font-bold flex items-center gap-1.5" style={{ color: "var(--text)" }}><Plus className="w-3.5 h-3.5 text-primary-c" /> Add team</button>
            </div>
            {form.teams.length === 0 && <p className="text-sm" style={{ color: "var(--text-2)" }}>Add two or more clubs to face off. You can expand to a multi-team league later.</p>}
            {form.teams.map((t) => (
              <TeamCard key={t.id} team={t} onName={(name) => patchTeam(t.id, { name })} onRemove={() => removeTeam(t.id)} onAddPlayer={(p) => addPlayer(t.id, p)} onRemovePlayer={(pid) => removePlayer(t.id, pid)} />
            ))}
          </div>
        )}

        {step === 3 && (
          <div className="space-y-4" data-testid="cw-step-lines">
            <div className="flex items-center justify-between">
              <h3 className="font-display font-bold text-lg">Match Categories & scoring</h3>
              <button data-testid="cw-add-line" onClick={addLine} className="glass rounded-full px-3 py-1.5 text-xs font-bold flex items-center gap-1.5" style={{ color: "var(--text)" }}><Plus className="w-3.5 h-3.5 text-primary-c" /> Add category</button>
            </div>
            <div className="rounded-xl glow-border p-3 flex items-center gap-3 flex-wrap" style={{ background: "var(--surface)" }}>
              <Trophy className="w-4 h-4 text-primary-c" />
              <span className="text-xs font-bold">Clean-sweep bonus</span>
              <input data-testid="cw-sweep-bonus" type="number" min="0" max="99" value={form.sweep_bonus} onChange={(e) => set("sweep_bonus", Math.max(0, Number(e.target.value) || 0))} className="w-20 rounded-lg glow-border bg-surface px-2 py-1 text-sm outline-none" style={{ color: "var(--text)" }} />
              <span className="text-xs" style={{ color: "var(--text-2)" }}>extra team points if one club wins every line</span>
            </div>
            <div className="rounded-xl glow-border p-4" style={{ background: "var(--surface)" }} data-testid="cw-schedule-mode-card">
              <div className="flex items-center gap-2 mb-2"><CalendarDays className="w-4 h-4 text-primary-c" /><span className="text-sm font-bold">Match play order</span></div>
              <Select value={form.schedule_mode} onValueChange={(v) => set("schedule_mode", v)}>
                <SelectTrigger data-testid="cw-schedule-mode" className="rounded-xl glow-border bg-surface" style={{ color: "var(--text)" }}><SelectValue /></SelectTrigger>
                <SelectContent className="glass" style={{ background: "var(--surface-glass)" }}>
                  {CW_SCHEDULE_MODES.map((o) => <SelectItem key={o.v} value={o.v} data-testid={`cw-schedule-mode-${o.v}`}>{o.l}</SelectItem>)}
                </SelectContent>
              </Select>
              <p className="text-[11px] mt-2" style={{ color: "var(--text-2)" }}>{CW_SCHEDULE_MODES.find((o) => o.v === form.schedule_mode)?.hint} Drag categories below to set which plays first.</p>
            </div>
            {form.lines.length === 0 && <p className="text-sm" style={{ color: "var(--text-2)" }}>Add the match categories that make up this dual meet (e.g. Novice Men's Doubles, Intermediate Women's Doubles).</p>}
            <Reorder.Group axis="y" values={form.lines} onReorder={(v) => set("lines", v)} className="space-y-4">
              {form.lines.map((l, i) => (
                <LineCard key={l.id} line={l} index={i} best_of={form.best_of} onPatch={(p) => patchLine(l.id, p)} onRemove={() => removeLine(l.id)} />
              ))}
            </Reorder.Group>
          </div>
        )}

        <div className="flex items-center justify-between mt-8">
          <button data-testid="cw-prev" onClick={() => setStep((s) => Math.max(1, s - 1))} disabled={step === 1}
            className="glass rounded-full px-5 py-2.5 text-sm font-bold flex items-center gap-2 disabled:opacity-40" style={{ color: "var(--text)" }}><ArrowLeft className="w-4 h-4" /> Back</button>
          {step < STEPS.length ? (
            <button data-testid="cw-next" onClick={() => canNext() ? setStep((s) => s + 1) : toast.error("Event name is required")}
              className="btn-glow rounded-full px-6 py-2.5 text-sm font-bold flex items-center gap-2">Next <ArrowRight className="w-4 h-4" /></button>
          ) : (
            <button data-testid="cw-save" onClick={save} disabled={busy}
              className="btn-glow rounded-full px-6 py-2.5 text-sm font-bold flex items-center gap-2 disabled:opacity-50">{busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Check className="w-4 h-4" />} {editing ? "Save changes" : "Create Club War"}</button>
          )}
        </div>
      </div>
    </div>
  );
}

function TeamCard({ team, onName, onRemove, onAddPlayer, onRemovePlayer }) {
  const [name, setName] = useState("");
  const [q, setQ] = useState("");
  const [results, setResults] = useState([]);
  const [searching, setSearching] = useState(false);

  const runSearch = async (val) => {
    setQ(val);
    if (!val || val.trim().length < 2) { setResults([]); return; }
    setSearching(true);
    try {
      const { data } = await api.get(`/players/directory`, { params: { q: val.trim() } });
      setResults((data || []).slice(0, 6));
    } catch (e) { setResults([]); }
    finally { setSearching(false); }
  };

  return (
    <div className="rounded-xl glow-border p-4" style={{ background: "var(--surface)" }} data-testid={`cw-team-${team.id}`}>
      <div className="flex items-center gap-2 mb-3">
        <input data-testid={`cw-team-name-${team.id}`} value={team.name} onChange={(e) => onName(e.target.value)} className="flex-1 rounded-lg bg-transparent glow-border px-3 py-2 text-sm font-bold outline-none focus:ring-2 focus:ring-lime-400" style={{ color: "var(--text)" }} />
        <button data-testid={`cw-remove-team-${team.id}`} onClick={onRemove} aria-label="Remove team" className="glass rounded-lg w-9 h-9 flex items-center justify-center"><Trash2 className="w-4 h-4 text-red-400" /></button>
      </div>

      {team.players.length > 0 && (
        <div className="flex flex-wrap gap-2 mb-3" data-testid={`cw-roster-${team.id}`}>
          {team.players.map((p) => (
            <span key={p.id} className="glass rounded-full pl-3 pr-1.5 py-1 text-xs flex items-center gap-1.5">
              {p.name}
              <button data-testid={`cw-remove-player-${p.id}`} onClick={() => onRemovePlayer(p.id)} className="w-5 h-5 rounded-full flex items-center justify-center hover:bg-red-500/20"><X className="w-3 h-3" /></button>
            </span>
          ))}
        </div>
      )}

      <div className="flex items-center gap-2">
        <input data-testid={`cw-player-name-${team.id}`} value={name} onChange={(e) => setName(e.target.value)}
          onKeyDown={(e) => { if (e.key === "Enter" && name.trim()) { onAddPlayer({ name: name.trim() }); setName(""); } }}
          placeholder="Type a player name" className="flex-1 rounded-lg bg-surface glow-border px-3 py-2 text-sm outline-none" style={{ color: "var(--text)" }} />
        <button data-testid={`cw-add-player-${team.id}`} onClick={() => { if (name.trim()) { onAddPlayer({ name: name.trim() }); setName(""); } }} className="glass rounded-lg px-3 py-2 text-xs font-bold flex items-center gap-1" style={{ color: "var(--text)" }}><Plus className="w-3.5 h-3.5 text-primary-c" /> Add</button>
      </div>

      <div className="mt-2">
        <div className="flex items-center gap-2 rounded-lg bg-surface glow-border px-3 py-2">
          <Search className="w-3.5 h-3.5" style={{ color: "var(--text-2)" }} />
          <input data-testid={`cw-search-${team.id}`} value={q} onChange={(e) => runSearch(e.target.value)} placeholder="…or search the Player Directory" className="flex-1 bg-transparent text-sm outline-none" style={{ color: "var(--text)" }} />
          {searching && <Loader2 className="w-3.5 h-3.5 animate-spin text-primary-c" />}
        </div>
        {results.length > 0 && (
          <div className="mt-1.5 space-y-1" data-testid={`cw-search-results-${team.id}`}>
            {results.map((r) => (
              <button key={r.user_id} data-testid={`cw-pick-${r.user_id}`}
                onClick={() => { onAddPlayer({ name: r.name, player_id: r.user_id, gender: r.profile?.gender }); setResults([]); setQ(""); }}
                className="w-full text-left glass rounded-lg px-3 py-1.5 text-sm hover:scale-[1.01] transition-transform flex items-center justify-between">
                <span>{r.name}</span>
                <Plus className="w-3.5 h-3.5 text-primary-c" />
              </button>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

function LineCard({ line, index, best_of, onPatch, onRemove }) {
  const controls = useDragControls();
  return (
    <Reorder.Item value={line} dragListener={false} dragControls={controls} className="rounded-xl glow-border p-4" style={{ background: "var(--surface)" }} data-testid={`cw-line-${line.id}`}>
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <button type="button" data-testid={`cw-line-drag-${line.id}`} onPointerDown={(e) => controls.start(e)} aria-label="Drag to reorder" className="cursor-grab active:cursor-grabbing touch-none" style={{ color: "var(--text-2)" }}><GripVertical className="w-4 h-4" /></button>
          <span className="text-xs mono uppercase tracking-wider" style={{ color: "var(--text-2)" }}>Category {index + 1}</span>
        </div>
        <button data-testid={`cw-remove-line-${line.id}`} onClick={onRemove} aria-label="Remove category" className="glass rounded-lg w-8 h-8 flex items-center justify-center"><Trash2 className="w-4 h-4 text-red-400" /></button>
      </div>
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        <div className="col-span-2 sm:col-span-1">
          <label className="text-[10px] uppercase font-bold" style={{ color: "var(--text-2)" }}>Skill level</label>
          <input data-testid={`cw-line-skill-${line.id}`} value={line.skill || ""} onChange={(e) => onPatch({ skill: e.target.value })} placeholder="e.g. Novice" className="w-full mt-1 rounded-xl glow-border bg-surface px-3 py-2.5 text-sm outline-none" style={{ color: "var(--text)" }} />
        </div>
        <div className="col-span-2 sm:col-span-1">
          <label className="text-[10px] uppercase font-bold" style={{ color: "var(--text-2)" }}>Discipline</label>
          <div className="mt-1">
            <Select value={line.type} onValueChange={(v) => onPatch({ type: v })}>
              <SelectTrigger data-testid={`cw-line-type-${line.id}`} className="rounded-xl glow-border bg-surface" style={{ color: "var(--text)" }}><SelectValue /></SelectTrigger>
              <SelectContent className="glass" style={{ background: "var(--surface-glass)" }}>
                {CW_LINE_TYPES.map((o) => <SelectItem key={o.v} value={o.v} data-testid={`cw-line-type-${line.id}-${o.v}`}>{o.l}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
        </div>
        <div>
          <label className="text-[10px] uppercase font-bold" style={{ color: "var(--text-2)" }}>Series</label>
          <div className="mt-1">
            <Select value={line.series || (line.format === "bo3" ? "bo3" : "single")} onValueChange={(v) => onPatch({ series: v, format: v === "bo3" ? "bo3" : "single" })}>
              <SelectTrigger data-testid={`cw-line-series-${line.id}`} className="rounded-xl glow-border bg-surface" style={{ color: "var(--text)" }}><SelectValue /></SelectTrigger>
              <SelectContent className="glass" style={{ background: "var(--surface-glass)" }}>
                {CW_SERIES.map((o) => <SelectItem key={o.v} value={o.v} data-testid={`cw-line-series-${line.id}-${o.v}`}>{o.l}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
        </div>
        <div>
          <label className="text-[10px] uppercase font-bold" style={{ color: "var(--text-2)" }}>Team points</label>
          <input data-testid={`cw-line-points-${line.id}`} type="number" min="0" max="99" value={line.points} onChange={(e) => onPatch({ points: Math.max(0, Number(e.target.value) || 0) })} className="w-full mt-1 rounded-xl glow-border bg-surface px-3 py-2.5 text-sm outline-none" style={{ color: "var(--text)" }} />
        </div>
      </div>
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mt-3">
        <div>
          <label className="text-[10px] uppercase font-bold" style={{ color: "var(--text-2)" }}>Entries / team</label>
          <input data-testid={`cw-line-entries-${line.id}`} type="number" min="1" max="8" value={line.entries || 1} onChange={(e) => onPatch({ entries: Math.max(1, Number(e.target.value) || 1) })} className="w-full mt-1 rounded-xl glow-border bg-surface px-3 py-2.5 text-sm outline-none" style={{ color: "var(--text)" }} />
        </div>
        <div>
          <label className="text-[10px] uppercase font-bold" style={{ color: "var(--text-2)" }}>Set to</label>
          <input data-testid={`cw-line-target-${line.id}`} type="number" min="1" max="99" value={line.set_target} onChange={(e) => onPatch({ set_target: Math.max(1, Number(e.target.value) || 11) })} className="w-full mt-1 rounded-xl glow-border bg-surface px-3 py-2.5 text-sm outline-none" style={{ color: "var(--text)" }} />
        </div>
        <div>
          <label className="text-[10px] uppercase font-bold" style={{ color: "var(--text-2)" }}>Win by</label>
          <input data-testid={`cw-line-winby-${line.id}`} type="number" min="1" max="5" value={line.set_win_by} onChange={(e) => onPatch({ set_win_by: Math.max(1, Number(e.target.value) || 2) })} className="w-full mt-1 rounded-xl glow-border bg-surface px-3 py-2.5 text-sm outline-none" style={{ color: "var(--text)" }} />
        </div>
        <div>
          <label className="text-[10px] uppercase font-bold" style={{ color: "var(--text-2)" }}>Cap (0=none)</label>
          <input data-testid={`cw-line-cap-${line.id}`} type="number" min="0" max="99" value={line.set_cap} onChange={(e) => onPatch({ set_cap: Math.max(0, Number(e.target.value) || 0) })} className="w-full mt-1 rounded-xl glow-border bg-surface px-3 py-2.5 text-sm outline-none" style={{ color: "var(--text)" }} />
        </div>
      </div>
      <div className="mt-2 text-[11px]" style={{ color: "var(--text-2)" }}>
        {(line.series === "round_robin")
          ? `Round robin — every pair plays every opposing pair · each game to ${line.set_target}, win by ${line.set_win_by}${line.set_cap ? `, cap ${line.set_cap}` : ""}`
          : (line.series === "bo3" || line.format === "bo3")
            ? `Best of ${best_of} sets · each set to ${line.set_target}, win by ${line.set_win_by}${line.set_cap ? `, cap ${line.set_cap}` : ""}`
            : `Single game to ${line.set_target}, win by ${line.set_win_by}${line.set_cap ? `, cap ${line.set_cap}` : ""}`}
        {isDoublesLine(line.type) ? " · doubles" : " · singles"}
      </div>
    </Reorder.Item>
  );
}
